import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { differenceInMilliseconds } from "date-fns";
import { useEffect, useRef } from "react";

type ClockInVisibilityState = "enabled" | "disabled" | "hidden";

type ClockInVisibilityReason =
  | "inside_geofence"
  | "near_shift_start"
  | "too_early_to_clock_in"
  | "transfer_shift_priority"
  | "shift_more_than_12_hours_away"
  | "outside_geofence"
  | "no_primary_action";

interface ClockInVisibility {
  state: ClockInVisibilityState;
  reason: ClockInVisibilityReason;
}

interface FooterPrimaryAction {
  action: "transferShift" | "clockIn" | "updateEta" | "sentHome" | "uploadTimesheet";
  enabled: boolean;
}

interface UseClockInVisibilityTrackingParams {
  state: ClockInVisibilityState;
  reason: ClockInVisibilityReason;
  shiftId: string;
  workerId: string;
  workplaceId: string;
}

interface TrackedState {
  state: ClockInVisibilityState;
  reason: ClockInVisibilityReason;
  startTimestamp: Date;
}

function getClockInVisibility(
  primaryAction: FooterPrimaryAction | null,
  inside: boolean
): ClockInVisibility {
  if (!primaryAction) {
    return { state: "hidden", reason: "no_primary_action" };
  }

  switch (primaryAction.action) {
    case "transferShift": {
      return { state: "hidden", reason: "transfer_shift_priority" };
    }

    case "clockIn": {
      if (!primaryAction.enabled) {
        return { state: "disabled", reason: "too_early_to_clock_in" };
      }

      return { state: "enabled", reason: inside ? "inside_geofence" : "near_shift_start" };
    }

    case "updateEta": {
      return { state: "hidden", reason: "outside_geofence" };
    }

    default: {
      return { state: "hidden", reason: "no_primary_action" };
    }
  }
}

function useClockInVisibilityTracking(params: UseClockInVisibilityTrackingParams): void {
  const { state, reason, shiftId, workerId, workplaceId } = params;

  const trackedStateRef = useRef<TrackedState>({
    state,
    reason,
    startTimestamp: new Date(),
  });

  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    const tracked = trackedStateRef.current;

    if (tracked.state === state && tracked.reason === reason) {
      return;
    }

    const durationMs = differenceInMilliseconds(new Date(), tracked.startTimestamp);

    logEvent(APP_V2_USER_EVENTS.CLOCK_IN_VISIBILITY_STATE_CHANGED, {
      shiftId,
      workerId,
      workplaceId,
      state: tracked.state,
      reason: tracked.reason,
      durationMs,
    });

    trackedStateRef.current = {
      state,
      reason,
      startTimestamp: new Date(),
    };
  }, [state, reason, shiftId, workerId, workplaceId]);

  // Cleanup reads from refs so it always has the latest values at unmount time,
  // avoiding stale closures from the empty dependency array.
  useEffect(() => {
    return () => {
      const tracked = trackedStateRef.current;
      const { shiftId, workerId, workplaceId } = paramsRef.current;
      const durationMs = differenceInMilliseconds(new Date(), tracked.startTimestamp);

      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_VISIBILITY_STATE_CHANGED, {
        shiftId,
        workerId,
        workplaceId,
        state: tracked.state,
        reason: tracked.reason,
        durationMs,
      });
    };
  }, []);
}

export {
  type ClockInVisibility,
  type ClockInVisibilityReason,
  type ClockInVisibilityState,
  type FooterPrimaryAction,
  getClockInVisibility,
  useClockInVisibilityTracking,
  type UseClockInVisibilityTrackingParams,
};
